import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useDocuments } from '@pages/Profile/DocumentManagement/DocumentManagementPage'
import { getNextSignupRoute } from '@pages/Signup/routes/getNextSignupRoute'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1 } from '@ui/Typography'

import { isDepositBeforeKYC } from '@utils/AccountAccess/accountKYCStatuses'
import { hasSubmittedEconomicProfileTest } from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { BasePaths } from '@utils/path'
import { isZero } from '@utils/validations'

import { isVerificationExpedited } from '@model/AccountDetailedDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'
import { TestFormValues } from '@model/Test'

import { useClientTestSubmission } from '@hooks/useClientTestSubmission'
import { scrollToTop } from '@hooks/useScrollToTop'
import { safeLocalStorage } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'
import { TestForm, TestFormRef, TestFormWrapper } from '@shared/templates/Test/TestForm'

import { LeftSection } from '../../Components/LeftSection'
import { SignupFormWrapper } from '../../Components/SignupFormWrapper'
import { SignupSections, SignupStep } from '../../Components/SignupStep'
import { SignupWrapper } from '../../Components/SignupWrapper'

const useEconomicProfileTestMutate = (clientId?: string) => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { product } = useProductReadContext()
  const { refreshAccount } = useAccountWriteContext()

  const { performSubmission, submitTest, uploadDocuments, isLoading } = useClientTestSubmission()

  const mutate = async (values: TestFormValues) => {
    await performSubmission(async () => {
      await submitTest(values, { clientId: clientId || '' })

      const accountApTestAnswers = await apiClient.getAccountAppTestAnswers(locale, product)

      await uploadDocuments(values, {
        categoryId: DocumentCategoryType.AppropriatenessTest,
        typeId: DocumentCategoryOptionType.TradingStatement,
        ticketIdOrTestId: accountApTestAnswers.id,
        isTicketId: false,
      })

      await refreshAccount(locale)
      scrollToTop()
    })
  }

  return { mutate, isLoading }
}

export const EconomicProfilePage: React.FC = () => {
  const { clientId } = useParams<{ clientId?: string }>()

  const childRef = useRef<TestFormRef>(null)

  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()

  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()

  const locale = useSessionLanguage()
  const navigate = useNavigate()

  const [formValues, setFormValues] = useState<TestFormValues>()
  const [step, setStep] = useState('')

  const mutation = useEconomicProfileTestMutate(clientId)

  const { data, isLoading: isLoadingTest } = useQuery({
    queryKey: queryKeys.signup.economicProfileTest(locale),
    queryFn: () => apiClient.getEconomicProfileTest(locale, product),
  })

  const documentsProps = useDocuments()
  const documents = documentsProps.data || []

  const sections = data?.sections || []
  const stepIndex = sections?.findIndex((x) => x.code === step) || 0
  const subStep = stepIndex + 1
  const sectionSize = sections?.length || 0

  useEffect(() => {
    window.onpopstate = () => refreshAccount(locale)
  }, [locale, refreshAccount])

  useEffect(() => {
    const [section] = data?.sections || []
    setStep(section?.code || '')
  }, [account, data?.sections])

  const handleSubmit = async (values: TestFormValues) => {
    await mutation.mutate(values)
  }

  const handleNextStep = (values: TestFormValues) => {
    const sections = data?.sections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex + 1]
    if (section?.code) {
      setStep(section.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  const handlePrevStep = (values: TestFormValues) => {
    const sections = data?.sections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex - 1]

    if (section?.code) {
      setStep(section.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  if (!isLoadingTest && hasSubmittedEconomicProfileTest(account, product)) {
    const isDocumentSkipStep = !!safeLocalStorage.getItem('hasAdditionalId')
    if (isDepositBeforeKYC(account) || isVerificationExpedited(account)) {
      navigate(getNextSignupRoute(account))
      return
    }

    if (isDocumentSkipStep) {
      navigate('/sign-up/confirm')
      return
    }

    const nextRoute = getNextSignupRoute(account)
    navigate(nextRoute)
    return
  }

  return (
    <SignupFormWrapper
      label={t('Sign up.Sign Up')}
      onBackToPreviousStep={
        !isZero(stepIndex)
          ? () => {
              childRef?.current?.goPrevStep()
            }
          : undefined
      }
      cancelPath={BasePaths.TakeAppTest}
    >
      <SignupStep
        accountTypeId={account?.type.id}
        subStep={{
          current: stepIndex <= sectionSize ? subStep : sectionSize,
          total: sectionSize,
        }}
        section={SignupSections.EconomicProfile}
      />
      <Loading showLoadingIcon isLoading={isLoadingTest}>
        <SignupWrapper>
          <LeftSection
            title={t('Economic Profile')}
            description={() => [
              <Body1 isParagraph medium>
                {t(
                  'Sign up.Please answer all questions with accurate information that reflects your current situation'
                )}
              </Body1>,
              <Body1 isParagraph>
                {t(
                  'Sign up.We will not contact any employer you have specified under any circumstances'
                )}
              </Body1>,
            ]}
          />
          <TestFormWrapper isLoading={mutation.isLoading}>
            {data && (
              <TestForm
                ref={childRef}
                data={data}
                step={step}
                values={formValues}
                onSubmit={handleSubmit}
                goPrevStep={handlePrevStep}
                goNextStep={handleNextStep}
                documents={documents}
              />
            )}
          </TestFormWrapper>
        </SignupWrapper>
      </Loading>
    </SignupFormWrapper>
  )
}
